/* cl-algolia.js — the part of the Algolia search restyle that CSS cannot do.
   Companion to assets/cl-algolia.css; the two ship together.

   It does exactly six things, and nothing that changes what the search returns:

   1. UN-ESCAPES THE PRICE. This store's money format is
        <span class="money conversion-bear-money">S${{amount_no_decimals}}</span>
      (the span is what the currency app hooks into). Algolia templates the format and
      escapes the result, so every price on /search renders as the literal text
      `<span class="money conversion-bear-money">S$3,990</span>` — in the cards AND in the
      "Price range" facet labels. Rebuilding the span also restores the hook the currency
      app needs, which is why the text is re-parsed rather than just stripped of tags.

   2. PUTS THE FILTER CONTROL WHERE THE DESIGN PUTS IT. Algolia renders the Filter button
      two bands above the controls row and the applied-filter chips INSIDE the facets
      column. Once that column is a drawer (see the CSS), chips living inside it would be
      invisible until opened, so the button moves into the controls row and the chips move
      out onto the page.

   3. GIVES THE DRAWER A BACKDROP, A CLOSE BUTTON AND ESCAPE. Algolia's markup has none of
      those, and a pseudo-element cannot be clicked to close anything.

   4. REBUILDS THE CARD as the collection card: vendor above the title, SKU below it, and the
      wishlist heart and quick-add button the other listing surfaces carry. This is the one part
      that needs data Algolia does not render — see section 4 for where it comes from, what it
      costs, and why the quick-add gate is answered server-side.

      It also CORRECTS THE PRICE off the base market, which is a workaround rather than a
      restyle and is marked as such at `setPrice`: the app's index holds SGD amounts and its
      template formats them with the presentment currency without converting, so /en-my showed
      "RM8,999.00 MYR" where the collection card for the same bag said MYR 37,224. The right
      figure rides along on the lookup the SKU already pays for. Delete it, and the template's
      `price`/`was` fields, once the app renders market prices correctly.

   5. CONVERTS THE PRICE SLIDER'S LABELS to the market's currency. Same root cause as the card
      prices in 4 — the index holds SGD — but with no product to look up, so it applies
      Shopify.currency.rate to the rail labels and drag tooltips. The refinement itself still
      travels in the index's currency, which is what keeps the filter correct. See §1b.

   6. REPLACES THE SORT CONTROL with the collection page's: Algolia's "Sorted by" label goes,
      and its <select> is hidden behind a button and menu built from that select's own options.
      The select stays the source of truth and still drives Algolia's handler. See §1c.

   WHY A MutationObserver: the widget is React — it re-renders the results, the stats and
   the facet panels on every keystroke and every refinement. Anything moved or injected has
   to be re-asserted afterwards. Every step below is idempotent and checks for its own
   output first, so re-running is free, and `busy` keeps our own writes from re-entering the
   observer.

   NOTHING HERE RUNS OFF /search: the root element only exists on the page that renders the
   instantsearch app block. */
(function () {
  if (window.__clAlgolia) return;
  window.__clAlgolia = 1;

  var ROOT_ID = 'algolia-shopify-instantsearch';
  var busy = false;

  var root = function () { return document.getElementById(ROOT_ID); };

  /* ── 1. price un-escaping ─────────────────────────────────────────────────────
     OPERATES ON TEXT NODES, NOT ELEMENTS, and that is the important part. A "Price range"
     facet row looks like this:

       <label class="ais-facet--label">
         <input type="checkbox" class="ais-facet--checkbox">
         "&lt;span class=…&gt;S$1,000&lt;/span&gt; - &lt;span …&gt;S$1,500&lt;/span&gt;"
         <span class="ais-facet--count-distinct">25</span>
       </label>

     — the escaped money is a bare text node with an input BEFORE it and the count span
     AFTER it. Rebuilding the label's whole content from its textContent (the first version
     of this function did exactly that) silently deleted both: every facet checkbox vanished
     and the count merged into the value. Replacing only the offending text node cannot
     touch a sibling.

     STRICT BY DESIGN. A text node is only rebuilt when it consists of nothing but
     `<span class="...">plain text</span>` segments and plain text between them — no other
     attributes, no nesting, no leftover angle brackets. And the rebuild uses
     createElement/textContent, never innerHTML, so even a string that passes the test
     cannot inject markup. */
  var SPAN_RE = /<span class="([A-Za-z0-9 _-]*)">([^<>]*)<\/span>/g;

  /** Rebuilds one text node in place. Returns true if it was money markup. */
  function unescapeTextNode(node) {
    var text = node.nodeValue;
    if (!text || text.indexOf('<span') === -1) return false;

    var parts = [];
    var last = 0;
    var m;
    SPAN_RE.lastIndex = 0;
    while ((m = SPAN_RE.exec(text)) !== null) {
      if (m.index > last) parts.push({ text: text.slice(last, m.index) });
      parts.push({ cls: m[1], text: m[2] });
      last = m.index + m[0].length;
    }
    if (!parts.length) return false;
    if (last < text.length) parts.push({ text: text.slice(last) });

    // Any residue with markup in it means this is not the money format — leave it alone.
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].cls === undefined && /[<>]/.test(parts[i].text)) return false;
    }

    var frag = document.createDocumentFragment();
    for (var j = 0; j < parts.length; j++) {
      if (parts[j].cls === undefined) {
        frag.appendChild(document.createTextNode(parts[j].text));
      } else {
        var span = document.createElement('span');
        if (parts[j].cls) span.className = parts[j].cls;
        span.textContent = parts[j].text;
        frag.appendChild(span);
      }
    }
    node.parentNode.replaceChild(frag, node);
    return true;
  }

  /** Every text node under `el`, collected first — the walk cannot outlive its own edits. */
  function textNodes(el) {
    var out = [];
    var w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = w.nextNode())) if (n.nodeValue && n.nodeValue.indexOf('<span') !== -1) out.push(n);
    return out;
  }

  /* ── 1b. price slider: index amounts -> the market's currency ──────────────────
     The app indexes ONE set of amounts (this shop's base SGD) and its templates format them
     with the presentment currency WITHOUT converting — the same bug setPrice() already
     corrects on the hit cards. The range slider shows those raw numbers as its rail labels
     and drag tooltips, so /en-my offered "1200 – 42000" while every price on the page was in
     ringgit: a shopper filtering "under 5000" was filtering under 5000 SGD.

     THE FILTER ITSELF IS LEFT ALONE. Algolia refines on the indexed SGD values and must keep
     doing so — only the LABELS are converted, so the shopper reads and picks in their own
     currency while the query stays in the index's. Handle positions therefore still land
     where they did.

     RATE, NOT A LOOKUP: there is no product to ask, so this uses Shopify.currency.rate, the
     storefront's own base->presentment rate. That covers currency conversion but NOT a
     market's percentage price adjustment, so on a market that carries one the rail reads a
     few percent off the card prices. It is the closest a facet with no product behind it can
     get, and it is the difference between "roughly right" and "wrong by 3.2x".

     Formatting matches snippets/cl-money.liquid — "<ISO> <amount>", no cents, because every
     price in this catalogue is whole units. */
  var RATE = (function () {
    var c = (window.Shopify && window.Shopify.currency) || {};
    var r = parseFloat(c.rate);
    return r > 0 ? r : 1;
  })();
  var CODE = (function () {
    var c = (window.Shopify && window.Shopify.currency) || {};
    return c.active || '';
  })();

  function money(n, withCode) {
    var v = Math.round(n);
    var out;
    try {
      out = v.toLocaleString();
    } catch (e) {
      out = String(v);
    }
    return withCode && CODE ? CODE + ' ' + out : out;
  }

  /* THE CODE IS PRINTED ONCE, ON A NODE WE OWN (JC, 2026-08-14, second attempt). Version one
     put "SGD" in front of every rail label, which wrapped them onto two lines. Version two
     appended it to the facet heading instead and leaked onto Brand and Type as well: the
     heading classes this app block renders are shared across facet types, so no selector over
     THEIR markup is safe to write a currency into.

     So the code goes on an element this file creates, inside .ais-RangeSlider — which only the
     price facet ever renders, making it unambiguous by construction. The drag tooltip still
     carries the code inline: it appears alone, and it is the moment a shopper is choosing a
     number. */
  function labelCode(scope) {
    /* SELF-HEAL: strip the suffix version two wrote onto whatever it matched. React rebuilds
       these headings on its own eventually, but a shopper sitting on the page would otherwise
       keep reading "Brand (SGD)" until it did. */
    var tagged = scope.querySelectorAll('[data-cl-code]');
    for (var t = 0; t < tagged.length; t++) {
      var el = tagged[t];
      var kids = [].slice.call(el.childNodes);
      for (var k = 0; k < kids.length; k++) {
        var node = kids[k];
        if (node.nodeType !== 3 || !node.nodeValue || !node.nodeValue.trim()) continue;
        node.nodeValue = node.nodeValue.replace(/\s*\([A-Z]{3}\)\s*$/, '');
        break;
      }
      el.removeAttribute('data-cl-code');
    }

    if (!CODE) return;
    var sliders = scope.querySelectorAll('.ais-RangeSlider');
    for (var i = 0; i < sliders.length; i++) {
      var box = sliders[i];
      var note = box.querySelector('.cl-alg-cur');
      if (!note) {
        note = document.createElement('div');
        note.className = 'cl-alg-cur';
        box.insertBefore(note, box.firstChild);
      }
      var text = 'Amounts in ' + CODE;
      if (note.textContent !== text) note.textContent = text;
    }
  }

  /* Rheostat rewrites these nodes on every drag frame, so the RAW number is parked on the
     element the first time it is seen and read back from there afterwards — re-parsing our own
     output would convert an already-converted number on the second pass. */
  function fixSliderLabels(scope) {
    labelCode(scope);
    var nodes = scope.querySelectorAll('.rheostat-value, .rheostat-tooltip');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var txt = (el.textContent || '').trim();
      if (!txt) continue;
      var done = el.getAttribute('data-cl-money');
      /* Still showing what we wrote last time — nothing to do. */
      if (done && done === txt) continue;
      var raw = parseFloat(txt.replace(/[^0-9.]/g, ''));
      if (!isFinite(raw)) continue;
      /* Our own output surviving a re-render: a grouping separator or the code can only come
         from us, since the library prints bare digits. Read the raw back rather than
         re-converting it. */
      if (/[^0-9.]/.test(txt)) {
        var stored = parseFloat(el.getAttribute('data-cl-raw'));
        if (isFinite(stored)) raw = stored;
        else continue;
      }
      el.setAttribute('data-cl-raw', String(raw));
      var withCode = el.className.indexOf('rheostat-tooltip') !== -1;
      var next = money(raw * RATE, withCode);
      el.textContent = next;
      el.setAttribute('data-cl-money', next);
    }
  }

  /* ── 1c. sort: Algolia's <select> re-dressed as the collection page's menu ─────
     Algolia renders `<div class="ais-sort">Sorted by <select class="ais-SortBy-select">`. Two
     problems with shipping that: the standing "Sorted by" label is a word the collection page
     does not print (its button reads "Sort: Newest", which says the same thing in the space of
     a control), and a native select drops an OS menu next to a page whose every other dropdown
     is the theme's own white panel.

     THE SELECT REMAINS THE SOURCE OF TRUTH. It is hidden, never removed, and choosing an option
     writes to it and dispatches a real `change` — which is what React is listening for, so
     sorting keeps working through Algolia's own handler. Nothing here knows the index names or
     re-implements the query; a new sort option added in the dashboard appears in this menu on
     its own.

     Rebuilt from the select on every pass, because React replaces this subtree whenever the
     results change and our panel goes with it. Cheap: it is three nodes and a loop over the
     options. */
  var SORT_LABEL = 'Sort: ';

  function sortClose(wrap) {
    wrap.classList.remove('is-open');
    var b = wrap.querySelector('.cl-alg-sort__btn');
    if (b) b.setAttribute('aria-expanded', 'false');
  }

  function dressSort(page) {
    if (!page) return;
    var host = page.querySelector('.ais-sort');
    if (!host) return;
    var sel = host.querySelector('select');
    if (!sel) return;

    /* "Sorted by" is a bare text node beside the select — there is no element to hide, so it
       has to go in JS. Element labels are handled by the CSS as well, in case a future version
       of the app block wraps it. */
    var kids = [].slice.call(host.childNodes);
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].nodeType === 3 && kids[i].nodeValue && kids[i].nodeValue.trim()) host.removeChild(kids[i]);
    }

    var current = sel.options[sel.selectedIndex];
    var currentText = current ? current.textContent : '';

    var wrap = host.querySelector('.cl-alg-sort');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'cl-alg-sort';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cl-alg-sort__btn';
      btn.setAttribute('aria-haspopup', 'listbox');
      btn.setAttribute('aria-expanded', 'false');
      var lbl = document.createElement('span');
      lbl.className = 'cl-alg-sort__txt';
      btn.appendChild(lbl);
      /* Same chevron as .cl-plp__btn-chev on the collection page, drawn rather than linked so
         the control needs no asset. */
      btn.insertAdjacentHTML(
        'beforeend',
        '<svg class="cl-alg-sort__chev" width="13" height="13" viewBox="0 0 24 24" fill="none" ' +
          'stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M5 9l7 7 7-7"/></svg>'
      );
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var open = wrap.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      var menu = document.createElement('div');
      menu.className = 'cl-alg-sort__menu';
      menu.setAttribute('role', 'listbox');

      wrap.appendChild(btn);
      wrap.appendChild(menu);
      host.appendChild(wrap);
    }

    var txt = wrap.querySelector('.cl-alg-sort__txt');
    if (txt) txt.textContent = SORT_LABEL + currentText;

    /* Options are re-listed only when they actually differ, so an open menu is not rebuilt
       under the pointer on every keystroke. */
    var menuEl = wrap.querySelector('.cl-alg-sort__menu');
    var signature = [].map.call(sel.options, function (o) { return o.value + '|' + o.textContent; }).join('~');
    if (menuEl.getAttribute('data-cl-sig') !== signature) {
      menuEl.setAttribute('data-cl-sig', signature);
      menuEl.innerHTML = '';
      [].forEach.call(sel.options, function (o) {
        var item = document.createElement('button');
        item.type = 'button';
        item.setAttribute('role', 'option');
        item.textContent = o.textContent;
        item.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          sortClose(wrap);
          if (sel.value === o.value) return;
          sel.value = o.value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        });
        menuEl.appendChild(item);
      });
    }
    /* Marking the selected row is a per-pass job: the value changes without the option list
       changing, so it cannot live in the block above. */
    [].forEach.call(menuEl.children, function (item, i) {
      var o = sel.options[i];
      if (o && o.value === sel.value) item.classList.add('is-active');
      else item.classList.remove('is-active');
    });
  }

  /* Click-away and Escape, bound once off document: the panel itself is replaced by React, so
     a listener on it would die with the node. */
  document.addEventListener('click', function (e) {
    var open = document.querySelector('.cl-alg-sort.is-open');
    if (!open) return;
    if (e.target && e.target.closest && e.target.closest('.cl-alg-sort') === open) return;
    sortClose(open);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var open = document.querySelector('.cl-alg-sort.is-open');
    if (open) sortClose(open);
  });

  function fixPrices(scope) {
    var sels = [
      '.ais-hit--price',            // card price (the money markup sits inside a <b>)
      '.ais-facet-price_range',     // "Price range" facet rows
      '.ais-current-refined-values-container', // applied-filter chips repeat the value
      '.ais-CurrentRefinements'
    ];
    var n = 0;
    for (var i = 0; i < sels.length; i++) {
      var hosts = scope.querySelectorAll(sels[i]);
      for (var j = 0; j < hosts.length; j++) {
        var nodes = textNodes(hosts[j]);
        for (var k = 0; k < nodes.length; k++) if (unescapeTextNode(nodes[k])) n++;
      }
    }
    return n;
  }

  /* ── 2. drawer plumbing ────────────────────────────────────────────────────────
     The facets column is MOVED OUT of the app block into a host appended to <body>.
     MEASURED REASON: left where Algolia puts it, the open drawer painted underneath the
     promo bar (z-index 30) and the sticky header (25) even at z-index 2000000000 — the
     "Filters" head sat behind the header. Raising the z-index further changed nothing and
     no ancestor declares transform / filter / contain / will-change, so the section wrapper
     is painting the subtree at its own level regardless. The theme's own .cl-plp__drawer is
     a child of <body> (`closest('.shopify-section')` on it returns null), which is exactly
     why IT paints above the header — so the drawer follows the same route.
     React keeps rendering into the facet panels after the move: it owns their children, not
     their position in the document. */
  var HOST_ID = 'cl-alg-drawer-host';

  function host() {
    var h = document.getElementById(HOST_ID);
    if (!h) {
      h = document.createElement('div');
      h.id = HOST_ID;
      document.body.appendChild(h);
    }
    return h;
  }

  /** The facets column, wherever it currently lives — inside the widget or in the host. */
  function facets(r) {
    return document.getElementById(HOST_ID)
      ? document.querySelector('#' + HOST_ID + ' .ais-facets') || r.querySelector('.ais-facets')
      : r.querySelector('.ais-facets');
  }

  function isOpen(r) {
    var f = facets(r);
    return !!f && (f.classList.contains('cl-alg-open') || f.classList.contains('ais-facets__shown'));
  }

  function setOpen(r, open) {
    var f = facets(r);
    if (!f) return;
    // Both classes are toggled: Algolia's own button handler uses __shown below its `sm`
    // breakpoint, and leaving one of them set would jam the drawer half-open.
    f.classList.toggle('cl-alg-open', open);
    f.classList.toggle('ais-facets__shown', open);
    var ov = document.querySelector('#' + HOST_ID + ' .cl-alg-overlay');
    if (ov) ov.classList.toggle('is-open', open);
    var btn = r.querySelector('.ais-facets-button');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      var first = f.querySelector('.cl-alg-close');
      // preventScroll: the drawer is its own scroll container, and focusing into it can
      // otherwise scroll the first facet group past the head.
      if (first) first.focus({ preventScroll: true });
    } else if (btn && btn.offsetParent !== null) {
      // Focus goes back to the trigger — but only if it is actually focusable. A
      // display:none button silently swallows .focus() and leaves focus on <body>.
      btn.focus({ preventScroll: true });
    }
  }

  /* ── 2b. copy parity with the collection page ──────────────────────────────────
     Two of Algolia's strings say something different from the same control on a collection
     page: its button reads "Show filters" where ours reads "Filter" (and "show" is wrong for
     a toggle that also hides), and its heading reads "Search result" where the CL search
     page's heading is "Search".

     GUARDED BY AN EXACT MATCH so it can only ever touch Algolia's English defaults. If the
     app is localised, or Algolia changes its wording, the text will not match and is left
     exactly as it is rather than being overwritten with English. Delete this function if the
     Algolia wording should win. */
  var COPY = [
    { sel: '.ais-facets-button', from: 'Show filters', to: 'Filter' },
    { sel: '.ais-h2', from: 'Search result', to: 'Search' }
  ];

  function fixCopy(r) {
    for (var i = 0; i < COPY.length; i++) {
      var el = r.querySelector(COPY[i].sel) || document.querySelector('#' + HOST_ID + ' ' + COPY[i].sel);
      if (el && el.textContent.trim() === COPY[i].from) el.textContent = COPY[i].to;
    }
  }

  /* ── 4. card text: the collection page's stack ─────────────────────────────────
     A collection card reads vendor / title / SKU / price. An Algolia hit reads title /
     "<product type> by <vendor>" / price. Two of those three lines can be fixed from what is
     already on the page; the SKU cannot, because the app does not render it.

     THE VENDOR IS PARSED OUT OF THE SUBTITLE, and that is a compromise worth knowing about.
     Algolia templates that line as "<product type> by <vendor>" and marks up only the search
     matches inside it, so there is no vendor element to read — the name is the text after the
     last " by ". The split is guarded: no " by ", or nothing either side of it, and the
     subtitle is left exactly as it is rather than being mangled. If the app's template or
     its language ever changes, the cards quietly keep Algolia's line instead of breaking.

     ONE REQUEST FEEDS THREE FEATURES, so when it fails all three vanish at once and the cards
     look untouched rather than broken: no SKU line, no wishlist heart, no quick add. Two ways
     that used to happen silently, both now handled — an absolute hit link fetched cross-origin
     (a theme preview is a different origin from the shop's primary domain), and a failure cached
     for the rest of the session. Failures are held in memory with a timestamp and retried; only
     real answers are persisted; and the first failure warns once in the console with the URL to
     open, because the symptom otherwise says nothing about the cause.

     THE SKU AND THE WISHLIST HEART COST ONE TINY REQUEST PER PRODUCT, shared:
     `/products/<handle>?view=cl-hit`, an alternate template (templates/product.cl-hit.liquid)
     that renders one line of JSON — sku plus the three ids Swym's contract needs — so the
     response is ~120 bytes from Shopify's own cache rather than the ~20KB of product JSON a
     .js call returns. Answers are cached in sessionStorage — including the failures, or a
     product without them would be re-fetched on every keystroke, since React re-renders the
     whole list each time. A page of results is therefore ~12 requests once, then none.

     THE REAL FIX LIVES IN THE ALGOLIA DASHBOARD: add sku to the indexed attributes and put it
     in the hit template, and this whole lookup can be deleted in favour of reading the
     element. It is done here because the theme cannot reach into the app's configuration. */
  /* v3 (2026-08-13). The KEY IS VERSIONED, and that is not housekeeping: the first version
     cached failures under `cl-alg-hit:` for the whole session, so any browser that loaded this
     page while the template was unreachable kept an empty answer for every product until the
     tab was closed — and an empty answer means no SKU line, no heart and no quick-add button.
     Bumping the key retires those entries. Bump it again if the shape below changes.

     AND IT IS SCOPED TO THE MARKET, as of v3. Everything cached before was market-invariant — a
     SKU and three ids are the same in Kuala Lumpur as in Singapore — but the price added in v3
     is not. Without the market in the key, a shopper who switched country would be served the
     previous market's currency out of sessionStorage, which is the very bug this is here to
     fix, only harder to see because it needs a country switch to reproduce. The locale root and
     the active currency both go in, because either can change while the other does not. */
  var MARKET = (function () {
    var s = window.Shopify || {};
    var root = (s.routes && s.routes.root) || '/';
    var cur = (s.currency && s.currency.active) || '';
    return root + '|' + cur;
  })();
  var INFO_KEY = 'cl-alg-hit3:' + MARKET + ':';
  var infoMem = {};
  var infoWait = {};
  var infoFails = {};
  var NO_INFO = {};
  var FAIL_RETRY_MS = 60000;
  var FAIL_MAX = 2;

  /* ONLY REAL ANSWERS ARE PERSISTED. A product genuinely without a SKU is an answer and is
     worth remembering; a request that failed is not, and caching it turns a moment of bad
     network — or a template mid-deploy — into a page that stays broken. Failures are held in
     memory with a timestamp instead, so the next render retries after a minute, and capped so a
     truly missing template cannot be hammered on every keystroke. */
  function infoStore(handle, value, ok) {
    infoMem[handle] = value;
    if (!ok) {
      infoFails[handle] = { at: new Date().getTime(), n: (infoFails[handle] ? infoFails[handle].n : 0) + 1 };
      return;
    }
    delete infoFails[handle];
    try { sessionStorage.setItem(INFO_KEY + handle, JSON.stringify(value)); } catch (e) {}
  }

  function infoRead(handle) {
    var f = infoFails[handle];
    if (f) {
      /* Worth another try, so report "unknown" and let the caller fetch again. */
      if (f.n < FAIL_MAX && new Date().getTime() - f.at > FAIL_RETRY_MS) return null;
      return NO_INFO;
    }
    if (handle in infoMem) return infoMem[handle];
    var raw = null;
    try { raw = sessionStorage.getItem(INFO_KEY + handle); } catch (e) {}
    if (raw === null) return null;
    var v;
    try { v = JSON.parse(raw); } catch (e) { v = NO_INFO; }
    infoMem[handle] = v;
    return v;
  }

  var infoWarned = false;
  function infoWarn(url, why) {
    /* Once per page, not once per card: 12 identical warnings would bury the one that matters.
       It is a warning at all because the failure is otherwise INVISIBLE — the cards simply come
       back without a SKU, a heart or a quick add, and nothing says why. */
    if (infoWarned) return;
    infoWarned = true;
    if (window.console && console.warn) {
      console.warn(
        '[cl-algolia] Could not read product data for the search cards (' + why + '): ' + url +
          '\nThe SKU line, the wishlist heart and quick add all come from this. Open that URL ' +
          'directly — it should return one line of JSON. If it returns a page, ' +
          'templates/product.cl-hit.liquid is missing from the live theme.'
      );
    }
  }

  /** Fetches once per handle per session. `cb` always runs, with NO_INFO on any failure. */
  function infoFetch(path, handle, cb) {
    if (infoWait[handle]) { infoWait[handle].push(cb); return; }
    infoWait[handle] = [cb];
    var url = path + '?view=cl-hit';
    var done = function (value, ok) {
      infoStore(handle, value, ok);
      var waiting = infoWait[handle];
      infoWait[handle] = null;
      for (var i = 0; i < waiting.length; i++) waiting[i](value);
    };
    if (!window.fetch) { done(NO_INFO, false); return; }
    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) { infoWarn(url, 'HTTP ' + res.status); return ''; }
        return res.text();
      })
      .then(function (text) {
        var raw = (text || '').trim();
        if (!raw) { done(NO_INFO, false); return; }
        var v = null;
        /* Anything unparseable — a redirect, an error page, the layout no longer suppressed —
           is not an answer: the card keeps what Algolia gave it, and this is retried later. */
        try { v = JSON.parse(raw); } catch (e) {}
        if (!v || typeof v !== 'object') {
          infoWarn(url, 'the response was not JSON');
          done(NO_INFO, false);
          return;
        }
        done(v, true);
      })
      .catch(function (err) {
        infoWarn(url, err && err.message ? err.message : 'the request failed');
        done(NO_INFO, false);
      });
  }

  /* THE PRICE THE APP RENDERS IS WRONG OFF THE BASE MARKET, so it is overwritten with the one
     templates/product.cl-hit.liquid answered — see the long note there for why the app's figure
     cannot be salvaged arithmetically. This is a WORKAROUND OVER A THIRD-PARTY APP'S OUTPUT and
     should be deleted, along with the template's `price` and `was` fields, the day the Algolia
     app renders market prices correctly.

     ONLY THE TEXT IS REPLACED, never the structure. The app's own elements are kept exactly
     where they are — the <b>, the struck compare-at, the "-20%" pill — because the card's
     layout, its CSS (including the :has() rule that paints a discounted price maroon) and its
     re-render all belong to the app. Rebuilding the row here would mean owning all three.

     The discount pill is left alone deliberately: it states a PERCENTAGE, which is the same
     number in every currency, so it is right even while the amounts beside it are wrong.

     A missing or empty answer leaves the app's price untouched. That is the safe direction — a
     stale-currency price is bad, but an empty price row on a shopping page is worse, and this
     is exactly what happens when the lookup fails. */
  function setPrice(hit, info) {
    if (!info || !info.price) return;
    var row = hit.querySelector('.ais-hit--price');
    if (!row) return;

    /* The live price is the app's <b>. The fallback is not defensive padding: without it, a
       template change that dropped the <b> would leave this writing nothing while still marking
       the row done, and the page would go back to showing SGD amounts with no sign of why. So
       when there is no <b>, the row's own direct text node is rewritten instead — direct only,
       which cannot touch the struck price or the discount pill, since both are elements. */
    var live = row.querySelector('b');
    if (live) {
      if (live.textContent !== info.price) live.textContent = info.price;
    } else {
      var wrote = false;
      for (var i = 0; i < row.childNodes.length; i++) {
        var n = row.childNodes[i];
        if (n.nodeType === 3 && n.nodeValue && n.nodeValue.trim()) {
          if (!wrote) { n.nodeValue = info.price; wrote = true; }
          else n.nodeValue = '';
        }
      }
      if (!wrote) return; // Nothing to write into — leave it unmarked so the next pass retries.
    }
    /* Marks the row as ours so the pass above can tell a card that has been corrected from one
       the app has just re-rendered. React builds a fresh row on every re-render, and a fresh row
       carries no marker — which is exactly the signal needed. */
    row.setAttribute('data-cl-price', '1');

    /* The struck price only exists on a discounted hit. `was` is only sent on a genuine
       markdown, so if the app shows one and we have none, the two disagree about whether this
       product is on sale — leave the app's, rather than blanking a line and leaving a gap. */
    var was = row.querySelector('.ais-hit--price-striked');
    if (was && info.was && was.textContent !== info.was) was.textContent = info.was;
  }

  function setSku(el, value) {
    if (!el || !el.isConnected) return;
    if (!value) { el.remove(); return; }
    if (el.textContent !== value) el.textContent = value;
  }

  /* The wishlist heart. Identical markup and identical data attributes to the one
     snippets/cl-product-card.liquid emits, because the handler is the same one — the layout
     renders snippets/cl-card-wish.liquid on this template for exactly that reason. Built with
     createElement and setAttribute rather than innerHTML: the ids come from a fetched document,
     and nothing fetched should ever be parsed as markup.

     The icon URL comes from window.CL_ALGOLIA (set in layout/theme.liquid) because a static
     asset cannot call asset_url. No config, no heart — the alternative is a button with an
     invisible mask, which looks like a bug rather than a missing setting. */
  function addHeart(hit, info) {
    if (!window.CL_ALGOLIA || !window.CL_ALGOLIA.wishIcon) return;
    if (!info || !info.empi || !info.epi) return;
    if (hit.querySelector('.cl-card__wish')) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cl-card__wish';
    btn.setAttribute('aria-label', 'Add to wishlist');
    btn.setAttribute('data-epi', String(info.epi));
    btn.setAttribute('data-empi', String(info.empi));
    btn.setAttribute('data-du', info.du || '');

    var mask = "url('" + window.CL_ALGOLIA.wishIcon + "') center/contain no-repeat";
    var icon = document.createElement('span');
    icon.className = 'cl-mask cl-card__wish-icon';
    icon.style.webkitMask = mask;
    icon.style.mask = mask;
    btn.appendChild(icon);
    hit.appendChild(btn);

    /* Swym answers "what is already in the list" after first paint, and these hearts arrive
       later still — so the remembered answer is painted on now rather than assumed absent. */
    if (window.clCardWish && window.clCardWish.sync) window.clCardWish.sync(hit);
  }

  /* The quick-add button. Same markup, same class and same data attributes as the one
     snippets/cl-quick-add.liquid emits, so assets/cl-quickadd.js — loaded globally — drives it
     with no knowledge of this page, and the button on a search card behaves exactly like the
     button on a collection card.

     THE GATE IS SERVER-SIDE. `qa` comes from the same cl-quick-add-ok.liquid snippet the
     collection cards use, answered by templates/product.cl-hit.liquid, because a hit's markup
     carries no tags, no availability and no price for the browser to judge by. If the answer is
     not '1' there is no button — never a button that fails on tap.

     Built with createElement, never innerHTML: the values come from a fetched document. */
  function addQuickAdd(hit, info) {
    var cfg = window.CL_QUICKADD;
    if (!cfg || !info || info.qa !== '1' || !info.epi) return;
    if (hit.querySelector('[data-cl-qa]')) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cl-card__add';
    btn.setAttribute('data-cl-qa', '');
    btn.setAttribute('data-variant-id', String(info.epi));
    btn.setAttribute('data-label', cfg.label || 'Add to cart');
    btn.setAttribute('data-label-adding', cfg.labelAdding || 'Adding…');
    btn.setAttribute('data-label-added', cfg.labelAdded || 'In cart');
    btn.setAttribute('title', cfg.label || 'Add to cart');
    btn.setAttribute('aria-label', (cfg.label || 'Add to cart') + ': ' + (info.title || ''));

    if (!cfg.icon) return;
    var mask = "url('" + cfg.icon + "') center/contain no-repeat";
    var icon = document.createElement('span');
    icon.className = 'cl-mask cl-card__add-icon';
    icon.style.webkitMask = mask;
    icon.style.mask = mask;
    btn.appendChild(icon);

    /* ON THE HIT, not in its details block: the button is positioned against the heart, inside
       the top corner of the photo, and both are absolute against .ais-hit. */
    hit.appendChild(btn);

    /* Cards arrive after the page told cl-quickadd.js what is in the cart, so the remembered
       state is painted on now rather than assumed absent — the same reason the heart syncs. */
    if (window.clQuickAdd && window.clQuickAdd.sync) window.clQuickAdd.sync(hit);
  }

  function fixCards(r) {
    var hits = r.querySelectorAll('.ais-hit');
    for (var i = 0; i < hits.length; i++) {
      var hit = hits[i];
      var details = hit.querySelector('.ais-hit--details');
      var title = details && details.querySelector('.ais-hit--title');
      if (!details || !title) continue;

      var sub = details.querySelector('.ais-hit--subtitle');

      /* Vendor above the title, as .cl-card__brand sits above .cl-card__title. */
      if (sub && !details.querySelector('.cl-hit__brand')) {
        var text = sub.textContent.replace(/\s+/g, ' ').trim();
        var at = text.lastIndexOf(' by ');
        var vendor = at > 0 ? text.slice(at + 4).trim() : '';
        if (vendor) {
          var brand = document.createElement('strong');
          brand.className = 'cl-hit__brand';
          brand.textContent = vendor;
          details.insertBefore(brand, title);
          /* Only now is the subtitle redundant. Hidden by class rather than removed, so a
             re-render that brings it back cannot leave the card with a line we hid once. */
          sub.classList.add('cl-hit__sub-off');
        }
      }

      /* SKU under the title (the row the subtitle used to hold) and the wishlist heart —
         both from the same one lookup, since both need data the app does not render. */
      var needSku = !details.querySelector('.cl-hit__sku');
      var needHeart = !hit.querySelector('.cl-card__wish');
      var needAdd = !hit.querySelector('[data-cl-qa]');
      var priceRow = hit.querySelector('.ais-hit--price');
      var needPrice = !!priceRow && !priceRow.hasAttribute('data-cl-price');
      if (needSku || needHeart || needAdd || needPrice) {
        var link = title.querySelector('a') || hit.querySelector('a[href*="/products/"]');
        var href = link && link.getAttribute('href');
        var m = href && href.match(/\/products\/([^/?#]+)/);
        if (m) {
          var handle = m[1];
          /* THE PATH IS REBUILT FROM THE LOCALE ROOT, not taken from the hit's own href.
             Two separate things go wrong otherwise, and the second one is why this changed:

             1. Algolia's hit links can be ABSOLUTE, and an absolute URL points at the shop's
                primary domain — which on a theme preview or a myshopify address is a different
                origin, so the fetch is blocked by CORS and every card silently loses its SKU,
                heart and quick add. (Taking the pathname alone already fixed this one.)
             2. Those links are NOT LOCALE-ROOTED. On /en-my the app still links /products/x, so
                a fetch of that path is answered in the PRIMARY market — right product, wrong
                currency, which would hand the price fix below the same SGD figure it exists to
                replace. Same lesson as the predictive-search endpoint in sections/cl-header.

             Shopify.routes.root is '/' or '/en-my/' and always carries its trailing slash. */
          var path = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') +
            'products/' + handle;
          var line = null;
          if (needSku) {
            line = document.createElement('p');
            line.className = 'cl-hit__sku';
            title.parentNode.insertBefore(line, title.nextSibling);
          }
          /* One closure per card, so a slow answer lands on its own card and not on
             whichever one happens to be first when it arrives. */
          var apply = (function (el, node) {
            return function (info) {
              if (el) setSku(el, (info && info.sku) || '');
              if (!node.isConnected) return;
              setPrice(node, info);
              addHeart(node, info);
              addQuickAdd(node, info);
            };
          })(line, hit);
          var cached = infoRead(handle);
          if (cached !== null) apply(cached);
          else infoFetch(path, handle, apply);
        }
      }
    }
  }

  function enhance() {
    var r = root();
    if (!r || busy) return;
    busy = true;
    try {
      var page = r.querySelector('.ais-page');
      var f = facets(r);
      if (!page || !f) return;

      // Out of the app block, into the body-level host (see the note above).
      var h = host();
      if (f.parentNode !== h) h.appendChild(f);

      // Backdrop — a sibling of the drawer, so it can be clicked.
      if (!h.querySelector('.cl-alg-overlay')) {
        var ov = document.createElement('div');
        ov.className = 'cl-alg-overlay';
        ov.addEventListener('click', function () { setOpen(r, false); });
        h.insertBefore(ov, f);
      }

      // Drawer head: title + ✕.
      if (!f.querySelector('.cl-alg-head')) {
        var head = document.createElement('div');
        head.className = 'cl-alg-head';
        var h = document.createElement('strong');
        /* Wording from the theme setting (CL_ALG_FILTER_TITLE, emitted into window.CL_ALGOLIA
           by layout/theme.liquid); 'Filters' is the fallback, so a shop that never touches the
           setting — or a page where the config failed to render — reads exactly as before. The
           capitals and tracking are CSS, not text: see .cl-alg-head strong in cl-algolia.css. */
        h.textContent = (window.CL_ALGOLIA && window.CL_ALGOLIA.filterTitle) || 'Filters';
        var x = document.createElement('button');
        x.type = 'button';
        x.className = 'cl-alg-close';
        x.setAttribute('aria-label', 'Close filters');
        x.textContent = '✕';
        x.addEventListener('click', function () { setOpen(r, false); });
        head.appendChild(h);
        head.appendChild(x);
        f.insertBefore(head, f.firstChild);
      }

      // Drawer foot: Algolia's own "clear refinements" + an Apply that closes. Algolia
      // applies a refinement the moment it is ticked, so there is nothing to submit — the
      // button exists because the PLP drawer has one there and a phone needs a full-width
      // way out.
      var foot = f.querySelector('.cl-alg-foot');
      if (!foot) {
        foot = document.createElement('div');
        foot.className = 'cl-alg-foot';
        var apply = document.createElement('button');
        apply.type = 'button';
        apply.className = 'cl-alg-apply';
        apply.textContent = 'Apply';
        apply.addEventListener('click', function () { setOpen(r, false); });
        foot.appendChild(apply);
        f.appendChild(foot);
      }
      // Algolia's reset lives at the top of the column; the design puts it in the footer.
      var clear = f.querySelector('.ais-clear-refinements-container');
      if (clear && clear.parentNode !== foot) foot.insertBefore(clear, foot.firstChild);

      // Filter button + sort as one actions cluster on the right of the controls row.
      var header = page.querySelector('.ais-search-header');
      var btn = r.querySelector('.ais-facets-button');
      if (header && btn) {
        var actions = header.querySelector('.cl-alg-actions');
        if (!actions) {
          actions = document.createElement('div');
          actions.className = 'cl-alg-actions';
          header.appendChild(actions);
        }
        if (btn.parentNode !== actions) {
          btn.setAttribute('role', 'button');
          btn.setAttribute('tabindex', '0');
          btn.setAttribute('aria-expanded', isOpen(r) ? 'true' : 'false');
          actions.appendChild(btn);
        }
        var sort = page.querySelector('.ais-sort');
        if (sort && sort.parentNode !== actions) actions.appendChild(sort);
        // Keep the cluster last in the row even if React re-appends a widget after it.
        if (actions.nextSibling) header.appendChild(actions);
      }

      // Applied-filter chips: out of the drawer, onto the page under the controls row.
      var chips = f.querySelector('.ais-current-refined-values-container, .ais-CurrentRefinements');
      if (chips && header && chips.parentNode === f) {
        header.parentNode.insertBefore(chips, header.nextSibling);
      }

      fixCopy(r);
      fixCards(r);

      // Both trees: the cards are in the widget, the price-range facet is in the host.
      fixPrices(r);
      fixPrices(h);
      fixSliderLabels(h);
      fixSliderLabels(r);
      dressSort(page);
    } finally {
      busy = false;
    }
  }

  /* ── 3. wiring ───────────────────────────────────────────────────────────────── */
  /* The Filter button is bound by delegation off document, because React replaces nodes
     inside the widget and a listener bound to a node dies with it.
     CAPTURE PHASE, AND stopPropagation, BOTH LOAD-BEARING. Algolia's own handler on that
     button calls stopPropagation, so a bubble-phase listener on document never fires — the
     first version of this was byte-correct and simply never ran. Taking the event in
     capture also means Algolia's handler does not run at all, which is what keeps the two
     from toggling the same drawer twice per click (their toggle uses `ais-facets__shown`,
     which setOpen owns as well). */
  document.addEventListener('click', function (e) {
    var r = root();
    if (!r) return;
    var t = e.target && e.target.closest ? e.target.closest('.ais-facets-button') : null;
    if (!t || !r.contains(t)) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(r, !isOpen(r));
  }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var t = e.target && e.target.closest ? e.target.closest('.ais-facets-button') : null;
    var r = root();
    if (!t || !r || !r.contains(t)) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(r, !isOpen(r));
  }, true);
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var r = root();
    if (r && isOpen(r)) setOpen(r, false);
  });

  function start() {
    var r = root();
    if (!r) return;
    enhance();
    // React re-renders on every keystroke and refinement — re-assert after it settles.
    var t = null;
    var obs = new MutationObserver(function () {
      if (busy) return;
      clearTimeout(t);
      t = setTimeout(enhance, 60);
    });
    obs.observe(r, { childList: true, subtree: true });
    // The host as well: after the move, the facet panels React re-renders (and the escaped
    // price-range labels it re-renders with) are no longer inside the widget root.
    var h = document.getElementById(HOST_ID);
    if (h) obs.observe(h, { childList: true, subtree: true });
  }

  // The app block renders client-side, so the root may not exist yet at DOMContentLoaded.
  // Poll briefly, then stop — no timer is left running on a page that has no widget.
  function boot() {
    if (root()) { start(); return; }
    var tries = 0;
    var iv = setInterval(function () {
      if (root()) { clearInterval(iv); start(); }
      else if (++tries > 60) clearInterval(iv); // ~30s, then give up
    }, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
